import type { ReportingSeoData } from "./reporting"
import { deriveSeoReport } from "./reportingView"

export const WEEKLY_BRIEF_VERSION = "weekly-brief-v1"
const MAX_FINDINGS = 3
const MIN_REVIEW_CLICKS = 10
const MIN_PRIOR_CLICKS = 20
const MIN_RELATIVE_CHANGE = 0.2

type ReportModule = "seo" | "checks" | "incidents" | "deployments"
type CoverageState = "available" | "partial" | "missing" | "unconnected"

export interface WeeklyFinding {
  id: string
  productKey: string
  productName: string
  module: ReportModule
  title: string
  observation: string
  proposal: string
  successGate: string
  priority: number
  magnitude: number
  evidence: {
    start: string
    end: string
    periodBasis: "inclusive-source-dates" | "exclusive-end-timestamps" | "state-at-collection"
    sourceKeys: string[]
    collectionIds: string[]
  }
}

function coverage(state: CoverageState, detail: string) {
  return { state, detail }
}

/** Deterministic proposed review items, not owner-assigned decisions or provider mutations. */
export function deriveWeeklyBrief(data: ReportingSeoData, productKey?: string) {
  const views = deriveSeoReport(data).filter((view) => !productKey || view.product.key === productKey)
  const candidates: WeeklyFinding[] = []
  const rows = views.map((view) => {
    const product = view.product
    const operations = data.operations?.products.find((item) => item.productKey === product.key)
    const dates = [...view.priorDates, ...view.currentDates]
    const sourceDays = data.days.filter(
      (day) =>
        day.productKey === product.key &&
        day.grain === "property" &&
        dates.includes(day.date) &&
        day.date >= product.includedFrom,
    )
    const sourceKeys = view.sources.filter((source) => source.provider === "gsc").map((source) => source.key)
    const seoEvidence: WeeklyFinding["evidence"] = {
      start: dates[0] ?? data.range.start,
      end: dates.at(-1) ?? data.range.end,
      periodBasis: "inclusive-source-dates",
      sourceKeys,
      collectionIds: sourceDays.map((day) => day.collectionId),
    }
    const add = (finding: Omit<WeeklyFinding, "id" | "productKey" | "productName">, suffix: string) => {
      candidates.push({
        ...finding,
        productKey: product.key,
        productName: product.name,
        id: `${product.key}-${suffix}`,
      })
    }
    const currentComplete = view.current.complete
    const comparable = currentComplete && view.prior.complete
    const refreshFailures = sourceDays.filter((day) => day.latestAttemptStatus !== "available").length
    if (!comparable) {
      add(
        {
          module: "seo",
          title: "Search comparison is incomplete",
          observation: `Current search window has ${view.current.observedDays}/${view.current.expectedDays} observed days; prior window has ${view.prior.observedDays}/${view.prior.expectedDays}. This does not establish a traffic decline.`,
          proposal:
            "Verify the configured GSC property and collection access; restore the missing dated evidence before making a growth decision.",
          successGate: "Both matched seven-day property windows are complete through the AIX read API.",
          priority: 1,
          magnitude: 14 - view.current.observedDays - view.prior.observedDays,
          evidence: seoEvidence,
        },
        "seo-coverage",
      )
    } else if (refreshFailures > 0) {
      add(
        {
          module: "seo",
          title: "Search refresh needs attention",
          observation: `${refreshFailures} property-day refreshes in the comparison window failed or were partial. Earlier valid observations remain available; they are not fresh collection success.`,
          proposal:
            "Inspect the collection receipts and restore the failing source before relying on the next edition.",
          successGate: "A successful approved refresh is persisted and reconciled without losing earlier evidence.",
          priority: 1,
          magnitude: refreshFailures,
          evidence: seoEvidence,
        },
        "seo-refresh",
      )
    }
    if (
      comparable &&
      view.clickChange !== null &&
      view.percentChange !== null &&
      (view.prior.clicks ?? 0) >= MIN_PRIOR_CLICKS &&
      Math.abs(view.clickChange) >= MIN_REVIEW_CLICKS &&
      Math.abs(view.percentChange) >= MIN_RELATIVE_CHANGE
    ) {
      const rising = view.clickChange > 0
      add(
        {
          module: "seo",
          title: rising ? "Organic clicks increased" : "Organic clicks decreased",
          observation: `${view.current.clicks} vs ${view.prior.clicks} clicks (${rising ? "+" : ""}${view.clickChange}; ${(view.percentChange * 100).toFixed(1)}%) across matched seven-day source windows. This is an observation, not evidence of cause or conversion.`,
          proposal: rising
            ? "Inspect the page winners and intent before proposing additional SEO investment."
            : "Inspect the page decliners and dated release evidence before proposing a content change.",
          successGate:
            "A page-level explanation is supported by complete exports; any proposed change has a separate approval and measurement plan.",
          priority: rising ? 4 : 3,
          magnitude: Math.abs(view.clickChange),
          evidence: seoEvidence,
        },
        "seo-change",
      )
    }
    if (operations && data.operations) {
      const bindingKeys = data.operations.bindings.filter((binding) => binding.productKey === product.key)
      const operationalEvidence: WeeklyFinding["evidence"] = {
        start: data.operations.current.startInclusive,
        end: data.operations.current.endExclusive,
        periodBasis: "exclusive-end-timestamps",
        sourceKeys: [],
        collectionIds: [],
      }
      const exceptions = operations.checks.rows.filter(
        (row) => row.enabledAtCollection && (row.staleAtCollection === true || row.lastSuccess === false),
      )
      if (exceptions.length) {
        const production = exceptions.some((row) => ["prod", "production"].includes(row.environment))
        add(
          {
            module: "checks",
            title: "Check exceptions at collection",
            observation: `${exceptions.length} enabled checks had a failed latest result or stale collection state as of ${data.operations.collectedAt}. Environments: ${[...new Set(exceptions.map((row) => row.environment))].sort().join(", ")}. This is not a wall-clock uptime estimate.`,
            proposal:
              "Review these checks in the existing incident workflow; do not run probes or resolve incidents from this report.",
            successGate:
              "Current persisted results and incident ownership establish whether investigation or recovery is needed.",
            priority: production ? 0 : 2,
            magnitude: exceptions.length,
            evidence: {
              ...operationalEvidence,
              start: data.operations.collectedAt,
              end: data.operations.collectedAt,
              periodBasis: "state-at-collection",
              sourceKeys: exceptions.map((row) => row.bindingKey),
            },
          },
          "check-exceptions",
        )
      }
      const unavailable = (["checks", "incidents", "deployments"] as const).filter((module) =>
        operations[module].reasons.some((reason) =>
          ["SOURCE_UNAVAILABLE", "OWNERSHIP_MISMATCH", "ROW_CAP"].includes(reason),
        ),
      )
      if (unavailable.length) {
        add(
          {
            module: unavailable[0],
            title: "Operational evidence is incomplete",
            observation: `Stored ${unavailable.join(", ")} evidence includes unavailable, denied or capped sources. A missing source is not a healthy service.`,
            proposal:
              "Inspect source receipts and approved ownership mappings; do not broaden access to make the report green.",
            successGate:
              "The existing scoped reporting API returns verified evidence or an explicitly accepted source gap.",
            priority: 1,
            magnitude: unavailable.length,
            evidence: { ...operationalEvidence, sourceKeys: bindingKeys.map((binding) => binding.key) },
          },
          "operational-coverage",
        )
      }
      const deployExceptions = operations.deployments.rows.filter(
        (row) => row.current.failed + row.current.healthFailed + row.current.degraded > 0,
      )
      if (deployExceptions.length) {
        add(
          {
            module: "deployments",
            title: "Deployment outcomes warrant review",
            observation:
              deployExceptions
                .map(
                  (row) =>
                    `${row.environment}: ${row.current.failed} provider failures; ${row.current.healthFailed} failed and ${row.current.degraded} degraded health evaluations`,
                )
                .join(". ") + ". These are separate outcome categories, not a unique incident count.",
            proposal:
              "Inspect Deploy Feed evidence for the affected projects; do not infer customer impact from provider status alone.",
            successGate:
              "Affected deployments have an evidence-backed disposition, with provider success and evaluated health kept separate.",
            priority: 2,
            magnitude: deployExceptions.reduce(
              (sum, row) => sum + row.current.failed + row.current.healthFailed + row.current.degraded,
              0,
            ),
            evidence: { ...operationalEvidence, sourceKeys: deployExceptions.map((row) => row.bindingKey) },
          },
          "deployments",
        )
      }
      const unified = operations.incidents.currentUnified?.ongoingAtEnd ?? null
      const legacy = operations.incidents.currentLegacy?.ongoingAtEnd ?? null
      if ((unified ?? 0) > 0 || (legacy ?? 0) > 0) {
        add(
          {
            module: "incidents",
            title: "Incidents remained open at week end",
            observation: `Unified: ${unified ?? "unknown"}; legacy uptime: ${legacy ?? "unknown"} ongoing at the historical week boundary. These systems are not deduplicated and are not summed; present-day status may differ.`,
            proposal:
              "Review the incident records and current ownership before deciding whether follow-up is still required.",
            successGate:
              "Historical carryover is reconciled to current incident evidence without automatically resolving records.",
            priority: 2,
            magnitude: Math.max(unified ?? 0, legacy ?? 0),
            evidence: {
              ...operationalEvidence,
              sourceKeys: bindingKeys
                .filter((binding) => binding.kind === "incident-project" || binding.kind === "uptime")
                .map((binding) => binding.key),
            },
          },
          "incidents",
        )
      }
    }
    const seoState = currentComplete ? "available" : view.current.observedDays > 0 ? "partial" : "missing"
    return {
      productKey: product.key,
      productName: product.name,
      seo: coverage(
        seoState,
        `${view.current.observedDays}/${view.current.expectedDays} current source days; ${view.prior.observedDays}/${view.prior.expectedDays} prior`,
      ),
      checks: coverage(
        operations?.checks.status ?? "unconnected",
        operations?.checks.reasons.join(", ") || "Recorded-check evidence; not wall-clock availability",
      ),
      incidents: coverage(
        operations?.incidents.status ?? "unconnected",
        operations?.incidents.reasons.join(", ") || "Unified and legacy counts remain separate",
      ),
      deployments: coverage(
        operations?.deployments.status ?? "unconnected",
        operations?.deployments.reasons.join(", ") || "Provider outcomes and health evaluations remain separate",
      ),
      business: coverage("unconnected", "Paid, analytics and billing connectors are not integrated"),
    }
  })
  const ordered = candidates.sort(
    (a, b) => a.priority - b.priority || b.magnitude - a.magnitude || a.id.localeCompare(b.id),
  )
  // Production check exceptions always come first; then spread attention across products.
  const findings: WeeklyFinding[] = ordered.filter((item) => item.priority === 0).slice(0, MAX_FINDINGS)
  for (const candidate of ordered)
    if (!findings.some((item) => item.productKey === candidate.productKey) && findings.length < MAX_FINDINGS)
      findings.push(candidate)
  for (const candidate of ordered)
    if (!findings.includes(candidate) && findings.length < MAX_FINDINGS) findings.push(candidate)
  findings.sort((a, b) => a.priority - b.priority || b.magnitude - a.magnitude || a.id.localeCompare(b.id))
  return {
    version: WEEKLY_BRIEF_VERSION,
    findings,
    candidateCount: candidates.length,
    coverage: rows,
    productKeys: views.map((view) => view.product.key),
    thresholdNote: `Search review threshold: at least ${MIN_REVIEW_CLICKS} absolute clicks and ${MIN_RELATIVE_CHANGE * 100}% change, with at least ${MIN_PRIOR_CLICKS} prior clicks. These are review heuristics, not business targets.`,
  }
}
